import axios from "axios";

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  withCredentials: true,
});

// ─── CSRF Token Handling ─────────────────────────────────────────────────────
// Fetches a CSRF token from the server once, then attaches it to every
// state-changing request (POST, PUT, DELETE, PATCH) via the X-CSRF-Token header.
let csrfToken = null;
let csrfFetchPromise = null;

function fetchCsrfToken() {
  if (csrfFetchPromise) return csrfFetchPromise;
  csrfFetchPromise = api
    .get("/auth/csrf-token")
    .then((res) => {
      csrfToken = res.data.csrfToken;
      return csrfToken;
    })
    .catch(() => {
      // Non-fatal — will retry on next mutating request
      csrfFetchPromise = null;
      return null;
    });
  return csrfFetchPromise;
}

api.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "delete", "patch"].includes(method)) {
    if (!csrfToken) {
      await fetchCsrfToken();
    }
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

// If any response comes back as 403 with a CSRF error, refresh the token and retry once
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.error?.message === "Invalid or missing CSRF token" &&
      !original._csrfRetry
    ) {
      original._csrfRetry = true;
      csrfToken = null;
      csrfFetchPromise = null;
      await fetchCsrfToken();
      if (csrfToken) {
        original.headers["X-CSRF-Token"] = csrfToken;
        return api(original);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
